import type { ApproveSommelierDto, ChangeClientSommelierByUserDto, ChangeClientSommelierDto, ClientSommelierByUserDto, CreateSommelierDto, CreateSommelierWithUserDto, DisableSommelierWithReassignDto, GetSommelierClientsDto, GetSommelierDto, RegisterSommelierDto, SommelierClientReportDto, SommelierDeletionInfoDto, SommelierDto, UpdateSommelierDto, UpdateSommelierSlugDto, UpdateSommelierWithUserDto } from './models';
import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import type { OrderDto } from '../orders/models';
import type { PickUpDto } from '../pick-ups/models';
import type { IdentityUserDto } from '../volo/abp/identity/models';
import type { WineryDto } from '../wineries/models';

@Injectable({
  providedIn: 'root',
})
export class SommelierService {
  apiName = 'Default';
  

  approveSommelier = (input: ApproveSommelierDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierDto>({
      method: 'POST',
      url: '/api/app/sommelier/approve-sommelier',
      body: input,
    },
    { apiName: this.apiName,...config });

  rejectSommelier = (sommelierId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierDto>({
      method: 'POST',
      url: `/api/app/sommelier/reject-sommelier/${sommelierId}`,
    },
    { apiName: this.apiName,...config });
  

  create = (input: CreateSommelierDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierDto>({
      method: 'POST',
      url: '/api/app/sommelier',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  createWithUser = (input: CreateSommelierWithUserDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierDto>({
      method: 'POST',
      url: '/api/app/sommelier/with-user',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/app/sommelier/${id}`,
    },
    { apiName: this.apiName,...config });
  

  deleteFileByFileAttachmentId = (fileAttachmentId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, boolean>({
      method: 'DELETE',
      url: `/api/app/sommelier/file/${fileAttachmentId}`,
    },
    { apiName: this.apiName,...config });
  

  deliverOrder = (orderId: number, deliveryCode: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/sommelier/deliver-order/${orderId}`,
      params: { deliveryCode },
    },
    { apiName: this.apiName,...config });
  

  disable = (sommelierId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/sommelier/disable/${sommelierId}`,
    },
    { apiName: this.apiName,...config });


  // winepassport#349 / WP-003 — borrado de duplicados sin historial. ABP rutea el parámetro
  // "id" antes del nombre de la acción (a diferencia de "sommelierId" en getClientCount).
  getDeletionInfo = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierDeletionInfoDto>({
      method: 'GET',
      url: `/api/app/sommelier/${id}/deletion-info`,
    },
    { apiName: this.apiName,...config });


  hardDelete = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/sommelier/${id}/hard-delete`,
    },
    { apiName: this.apiName,...config });


  // winepassport#339 / CU-44 — reasignación administrativa
  getClientCount = (sommelierId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'GET',
      url: `/api/app/sommelier/client-count/${sommelierId}`,
    },
    { apiName: this.apiName,...config });


  changeClientSommelier = (input: ChangeClientSommelierDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: '/api/app/sommelier/change-client-sommelier',
      body: input,
    },
    { apiName: this.apiName,...config });


  disableWithReassign = (input: DisableSommelierWithReassignDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: '/api/app/sommelier/disable-with-reassign',
      body: input,
    },
    { apiName: this.apiName,...config });


  getClientSommelierByUser = (userId: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, ClientSommelierByUserDto>({
      method: 'GET',
      url: `/api/app/sommelier/client-sommelier-by-user/${userId}`,
    },
    { apiName: this.apiName,...config });


  changeClientSommelierByUser = (input: ChangeClientSommelierByUserDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: '/api/app/sommelier/change-client-sommelier-by-user',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  enable = (sommelierId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'POST',
      url: `/api/app/sommelier/enable/${sommelierId}`,
    },
    { apiName: this.apiName,...config });
  

  generateExcel = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, Blob>({
      method: 'GET',
      responseType: 'blob',
      url: '/api/app/sommelier/generate-excel',
    },
    { apiName: this.apiName,...config });
  

  get = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierDto>({
      method: 'GET',
      url: `/api/app/sommelier/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getBySlug = (slug: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierDto>({
      method: 'GET',
      url: '/api/app/sommelier/by-slug',
      params: { slug },
    },
    { apiName: this.apiName,...config });
  

  getFileByFileAttachmentId = (fileAttachmentId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Blob>({
      method: 'GET',
      responseType: 'blob',
      url: `/api/app/sommelier/file/${fileAttachmentId}`,
    },
    { apiName: this.apiName,...config });
  

  getList = (input: GetSommelierDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<SommelierDto>>({
      method: 'GET',
      url: '/api/app/sommelier',
      params: { keyword: input.keyword, status: input.status, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getMyClients = (input: GetSommelierClientsDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierClientReportDto[]>({
      method: 'GET',
      url: '/api/app/sommelier/my-clients',
      params: { from: input.from, to: input.to },
    },
    { apiName: this.apiName,...config });
  

  getMyClientsCount = (input?: GetSommelierClientsDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'GET',
      url: '/api/app/sommelier/my-clients-count',
      params: { from: input.from, to: input.to },
    },
    { apiName: this.apiName,...config });
  

  getMyDeliveries = (input?: GetSommelierClientsDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, OrderDto[]>({
      method: 'GET',
      url: '/api/app/sommelier/my-deliveries',
      params: { from: input.from, to: input.to },
    },
    { apiName: this.apiName,...config });
  

  getMySalesCount = (input?: GetSommelierClientsDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'GET',
      url: '/api/app/sommelier/my-sales-count',
      params: { from: input.from, to: input.to },
    },
    { apiName: this.apiName,...config });
  

  getMySlug = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, string>({
      method: 'GET',
      responseType: 'text',
      url: '/api/app/sommelier/my-slug',
    },
    { apiName: this.apiName,...config });
  

  getPickUpsBySommelierId = (sommelierId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PickUpDto[]>({
      method: 'GET',
      url: `/api/app/sommelier/pick-ups-by-sommelier-id/${sommelierId}`,
    },
    { apiName: this.apiName,...config });
  

  getUsers = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, IdentityUserDto[]>({
      method: 'GET',
      url: '/api/app/sommelier/users',
    },
    { apiName: this.apiName,...config });
  

  getWineries = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, WineryDto[]>({
      method: 'GET',
      url: '/api/app/sommelier/wineries',
    },
    { apiName: this.apiName,...config });
  

  registerSommelier = (input: RegisterSommelierDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierDto>({
      method: 'POST',
      url: '/api/app/sommelier/register-sommelier',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  update = (id: number, input: UpdateSommelierDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierDto>({
      method: 'PUT',
      url: `/api/app/sommelier/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });
  

  updateSommelier = (id: number, input: UpdateSommelierDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierDto>({
      method: 'PUT',
      url: `/api/app/sommelier/${id}/sommelier`,
      body: input,
    },
    { apiName: this.apiName,...config });
  

  updateSommelierSlug = (id: number, input: UpdateSommelierSlugDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierDto>({
      method: 'PUT',
      url: `/api/app/sommelier/${id}/sommelier-slug`,
      body: input,
    },
    { apiName: this.apiName,...config });
  

  updateWithUser = (id: number, input: UpdateSommelierWithUserDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierDto>({
      method: 'PUT',
      url: `/api/app/sommelier/${id}/with-user`,
      body: input,
    },
    { apiName: this.apiName,...config });
  

  uploadFileByFile = (file: FormData, config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'POST',
      url: '/api/app/sommelier/upload-file',
      body: file,
    },
    { apiName: this.apiName,...config });

  constructor(private restService: RestService) {}
}
